import { useRef, useEffect, useState } from "react";
import { Pin, Archive, Trash2, MoreHorizontal, Upload } from "lucide-react";
import { ActiveUseCaseBadge } from "./ActiveUseCaseBadge";
import { ModelDropdownTrigger, ModelDropdownList } from "./ModelDropdown";

const ChatHeaderMenu = ({
  onPinChat,
  onArchive,
  onDelete,
  isPinned = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setIsOpen(false);
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        title="More options"
      >
        <MoreHorizontal
          size={16}
          className="text-slate-500 dark:text-slate-400"
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 z-[101] min-w-[160px] bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200/80 dark:border-slate-700 py-1.5 animate-in fade-in zoom-in-95 duration-150 origin-top-right">
          <button
            onClick={() => {
              onPinChat?.();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-3 transition-colors"
          >
            <Pin size={14} className="text-slate-400" />
            {isPinned ? "Unpin Chat" : "Pin Chat"}
          </button>
          <button
            onClick={() => {
              onArchive?.();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-3 transition-colors"
          >
            <Archive size={14} className="text-slate-400" />
            Archive
          </button>
          <div className="mx-3 border-t border-slate-100 dark:border-slate-700 my-1" />
          <button
            onClick={() => {
              onDelete?.();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 transition-colors"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export const ChatHeader = ({
  activeUseCase,
  selectedModel,
  setSelectedModel,
  isModelDropdownOpen,
  setIsModelDropdownOpen,
  onShareChat,
  onPinChat,
  onArchiveChat,
  onDeleteChat,
  isPinned = false,
  conversationId,
  models = [],
}) => {
  const modelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modelRef.current && !modelRef.current.contains(e.target)) {
        setIsModelDropdownOpen(false);
      }
    };
    if (isModelDropdownOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isModelDropdownOpen]);

  return (
    <div className="w-full px-4 sm:px-8">
      <div className="flex items-center justify-between gap-3 w-full">
        {/* Left */}
        <div className="pointer-events-auto">
          <ActiveUseCaseBadge
            title={activeUseCase?.useCaseName || "General Chat"}
          />
        </div>

        {/* Right — pill */}

        <div className="pointer-events-auto">
          <div className="flex items-center bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200/60 dark:border-slate-600/50 shadow-sm rounded-full">
            {/* Upload */}
            <button
              onClick={onShareChat}
              className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-slate-100/80 dark:hover:bg-slate-700/70 transition-colors ml-0.5"
              title="Share"
            >
              <Upload
                size={15}
                className="text-slate-500 dark:text-slate-400"
              />
            </button>

            <div className="w-px h-5 bg-slate-200 dark:bg-slate-500/70" />

            {/* Model trigger */}
            <div className="relative" ref={modelRef}>
              <ModelDropdownTrigger
                selectedModel={selectedModel}
                isOpen={isModelDropdownOpen}
                onClick={() => setIsModelDropdownOpen((prev) => !prev)}
              />
              {isModelDropdownOpen && (
                <ModelDropdownList
                  models={models}
                  selectedModel={selectedModel}
                  setSelectedModel={setSelectedModel}
                  setIsOpen={setIsModelDropdownOpen}
                  conversationId={conversationId}
                />
              )}
            </div>

            <div className="w-px h-5 bg-white/80 dark:bg-slate-500/70" />

            {/* Three dot */}
            <div className="mr-0.5">
              <ChatHeaderMenu
                onPinChat={onPinChat}
                onArchive={onArchiveChat}
                onDelete={onDeleteChat}
                isPinned={isPinned}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
